import { useParams, Link, Navigate } from 'react-router-dom';
import { glossaryTerms, getGlossaryTermBySlug } from '@/content/glossary';
import { NotifyForm } from '@/components/NotifyForm';
import { useSeo } from '@/seo/useSeo';

export function GlossaryDetail(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const entry = getGlossaryTermBySlug(slug);

  useSeo({
    title: entry ? entry.term : 'Term not found',
    description: entry ? entry.shortDefinition : 'This glossary term could not be found.',
    path: `/glossary/${slug ?? ''}`,
    schema: entry
      ? {
          '@type': 'DefinedTerm',
          name: entry.term,
          description: [entry.shortDefinition, ...entry.explanation].join(' '),
          url: `https://nexusrmm.ai/glossary/${entry.slug}`,
          inDefinedTermSet: {
            '@type': 'DefinedTermSet',
            name: 'Nexus MSP glossary',
            url: 'https://nexusrmm.ai/glossary',
          },
        }
      : undefined,
  });

  if (!entry) return <Navigate to="/glossary" replace />;

  const Icon = entry.icon;
  const related = glossaryTerms.filter((t) => t.slug !== entry.slug).slice(0, 3);

  return (
    <div>
      <section className="relative overflow-hidden pt-16 pb-16 md:pt-24 md:pb-24">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-nexus-grid bg-grid [mask-image:radial-gradient(circle_at_50%_0%,#000,transparent_70%)]"
        />
        <div className="container-content relative max-w-[760px]">
          <p className="text-xs text-white/40">
            <Link to="/glossary" className="text-white/40 no-underline hover:text-nexus-purple-light">
              Glossary
            </Link>{' '}
            / {entry.term}
          </p>
          <div className="mb-5 mt-6 flex h-12 w-12 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light">
            <Icon fontSize="medium" />
          </div>
          <h1 className="font-display text-4xl font-bold leading-[1.1] tracking-tight text-nexus-ink md:text-[44px]">
            {entry.term}
          </h1>
          <p className="mt-5 text-lg text-white/70">{entry.shortDefinition}</p>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content max-w-[760px]">
          {entry.explanation.map((p, i) => (
            <p key={i} className="mt-4 text-[15px] leading-relaxed text-white/75 first:mt-0">
              {p}
            </p>
          ))}

          <div className="mt-8 rounded-2xl border border-nexus-purple/25 bg-nexus-purple/[0.06] p-5">
            <p className="text-xs font-bold uppercase tracking-[0.1em] text-nexus-purple-light">
              How Nexus handles this
            </p>
            <p className="mt-2 text-sm leading-relaxed text-white/75">{entry.howNexusHandlesIt}</p>
          </div>

          {entry.relatedTo && entry.relatedTo.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2.5">
              {entry.relatedTo.map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  className="rounded-full border border-white/15 px-3.5 py-1.5 text-xs font-semibold text-white/70 no-underline transition-colors hover:border-nexus-purple/50 hover:text-nexus-purple-light"
                >
                  {r.label} &rarr;
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">More terms</h2>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {related.map((t) => {
              const RelIcon = t.icon;
              return (
                <Link
                  key={t.slug}
                  to={`/glossary/${t.slug}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 no-underline transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50"
                >
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light">
                    <RelIcon fontSize="small" />
                  </div>
                  <h3 className="font-display text-[15px] font-semibold text-nexus-ink">{t.term}</h3>
                  <p className="mt-1.5 text-sm text-white/65">{t.shortDefinition}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
            <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
              Ready to see it in the platform?
            </h2>
            <p className="mx-auto mt-3 max-w-[520px] text-white/70">
              Join the design-partner cohort and we&apos;ll show you exactly where this lives.
            </p>
            <div className="mt-8 flex justify-center">
              <NotifyForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
